import { TaskStatus } from "@/types";
import { formatRelativeTimeFi } from "@/lib/format";
import { cn } from "@/lib/utils";
import { TASK_STATUS_ICONS, TASK_STATUS_COLORS, TASK_STATUS_LABELS_SINGULAR } from "@/lib/taskStatusMeta";

export function getNextStatus(currentStatus: TaskStatus): TaskStatus {
  switch (currentStatus) {
    case TaskStatus.PENDING:
      return TaskStatus.IN_PROGRESS;
    case TaskStatus.IN_PROGRESS:
      return TaskStatus.COMPLETED;
    case TaskStatus.COMPLETED:
      return TaskStatus.PENDING;
    default:
      return TaskStatus.PENDING;
  }
}

export function StatusCheckmark({
  status,
  onClick,
  justCompleted,
  startedAt,
  completedAt,
  disabled = false,
}: {
  status: TaskStatus;
  onClick: () => void;
  justCompleted?: boolean;
  startedAt?: string | null;
  completedAt?: string | null;
  disabled?: boolean;
}) {
  const base =
    "flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center transition-all duration-200 motion-safe:active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";
  const disabledCls = disabled && "opacity-40 cursor-not-allowed pointer-events-none";

  const fmt = (iso: string) => new Date(iso).toLocaleString("fi-FI");
  let tooltip = "Klikkaa vaihtaaksesi";
  if (status === TaskStatus.COMPLETED) {
    tooltip = completedAt
      ? `Valmistui ${formatRelativeTimeFi(completedAt)} (${fmt(completedAt)})`
      : "Valmis";
  } else if (status === TaskStatus.IN_PROGRESS) {
    tooltip = startedAt
      ? `Aloitettu ${formatRelativeTimeFi(startedAt)} (${fmt(startedAt)})`
      : "Kesken";
  } else if (status === TaskStatus.PENDING) {
    tooltip = "Ei aloitettu – klikkaa aloittaaksesi"
  } else if (status in TaskStatus) {
    tooltip = TASK_STATUS_LABELS_SINGULAR[status];
  } else {
    tooltip = TASK_STATUS_LABELS_SINGULAR[TaskStatus.UNKNOWN];
  }

  if (status === TaskStatus.COMPLETED) {
    const Icon = TASK_STATUS_ICONS[TaskStatus.COMPLETED];
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={cn(
          base,
          TASK_STATUS_COLORS[TaskStatus.COMPLETED].iconButton,
          justCompleted && "motion-safe:animate-[pop_0.45s_ease-out]",
          disabledCls,
        )}
        title={tooltip}
      >
        <Icon className="h-5 w-5" />
      </button>
    );
  }

  if (status in TaskStatus) {
    const Icon = TASK_STATUS_ICONS[status];
    return (
      <button onClick={onClick} disabled={disabled} className={cn(base, TASK_STATUS_COLORS[status].iconButton, disabledCls)} title={tooltip}>
        <Icon className="h-5 w-5" />
      </button>
    );
  }

  const Icon = TASK_STATUS_ICONS[TaskStatus.UNKNOWN];
  return (
    <button onClick={onClick} disabled={disabled} className={cn(base, TASK_STATUS_COLORS[TaskStatus.UNKNOWN].iconButton, disabledCls)} title={tooltip}>
      <Icon className="h-5 w-5" />
    </button>
  );
}
